import { Fragment, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWavePulse } from '@fortawesome/pro-regular-svg-icons'
import Typography from '@mui/material/Typography'
import { LoadingStateComponent } from '@labrav/common'
import { FundingActivity } from '../../api/fundingApi'
import { formatActivityTime } from '../../utils/format'
import { ScrollHint } from '../ScrollHint/ScrollHint'
import { useStyles } from './RecentFundingActivities.styles'

interface RecentFundingActivitiesProps {
  activities?: FundingActivity[]
  loading?: boolean
  onOpenRequest?: (activity: FundingActivity) => void
}

export const RecentFundingActivities = ({
  activities = [],
  loading = false,
  onOpenRequest,
}: RecentFundingActivitiesProps) => {
  const { classes } = useStyles()
  const listRef = useRef<HTMLDivElement>(null)

  // The service sends one flat sentence, so the company name is linked in place
  // by splitting the summary around the company_name it also sends — the words
  // stay exactly as the service wrote them, apart from a capitalised company
  // name followed by its fund request id.
  const renderSummary = (activity: FundingActivity) => {
    const company = activity.company_name
    if (!company || !onOpenRequest) return activity.summary
    const at = activity.summary.indexOf(company)
    if (at < 0) return activity.summary
    const name = company.charAt(0).toUpperCase() + company.slice(1)
    return (
      <>
        {activity.summary.slice(0, at)}
        <Typography
          component="button"
          type="button"
          className={classes.companyLink}
          onClick={() => onOpenRequest(activity)}
        >
          {activity.funding_application_id
            ? `${name} (${activity.funding_application_id})`
            : name}
        </Typography>
        {activity.summary.slice(at + company.length)}
      </>
    )
  }

  if (!loading && activities.length === 0) return null

  return (
    <section className={classes.card} data-testid="funding-recent-activities">
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <span className={classes.headerIcon}>
            <FontAwesomeIcon icon={faWavePulse} />
          </span>
          <Typography component="h2" className={classes.title}>
            Recent funding activities
          </Typography>
        </div>
      </div>

      {loading ? (
        <div className={classes.loading}>
          <LoadingStateComponent
            heading="Fetching recent activities"
            description="It may take a few moments, please wait..."
          />
        </div>
      ) : (
        <div className={classes.scrollWrap}>
          <div ref={listRef} className={classes.list}>
            {activities.map((activity, index) => (
              <Fragment key={activity.id}>
                {index > 0 && <span className={classes.divider} />}
                <div className={classes.item}>
                  <Typography component="span" className={classes.itemText}>
                    {renderSummary(activity)}
                  </Typography>
                  {formatActivityTime(activity.event_at) && (
                    <Typography component="span" className={classes.itemTime}>
                      {formatActivityTime(activity.event_at)}
                    </Typography>
                  )}
                </div>
              </Fragment>
            ))}
          </div>
          <ScrollHint scrollRef={listRef} contentKey={activities.length} />
        </div>
      )}
    </section>
  )
}

export default RecentFundingActivities
